<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2022 (original work) Open Assessment Technologies SA ;

    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import { IconBarButton, Input } from '@oat-sa-private/ui-elements';
    import { __, remToPx, ResizeObserver, generateElementId } from '@oat-sa-private/ui-core';
    import { debounce } from 'lodash';

    /**
     * Document Viewer (only supports PDFs for now)
     * @property {string} src - the URL of the document
     * @property {string} [title] - the document title
     * @property {string|number} [width] - the container width
     * @property {string|number} [height] - the container width
     * @property {number} [page] - to load the document on a given page
     * @property {Object} [options] - rendering options
     * @property {string} [options.workerSrc] - the path of the PDFJS web worker
     * @property {...string} - rest will be applied to the container element as HTML attributes
     * @fires ready {CustomEvent} when the document is loaded and renreded
     * @fires error {CustomEvent} if the document doesn't load
     * @fires pagechange {CustomEvent} when the page of the document changed
     */
    export let src;
    export let title;
    export let width = '100%';
    export let height = 'auto';
    export let page = 1;
    export let options = {
        workerSrc: 'pdf.worker.min.js'
    };
    let initialScaleDefined = false;
    let pageCount = 0;

    let scale = 1;
    const minScale = 0.25;
    const maxScale = 2;
    const scaleSteps = 0.25;

    const fullBarMiniumContainerWidth = 400;
    const documentWrapperId = generateElementId('doc');
    let containerElement;
    let canvasElement;
    let textLayerElement;
    let displayPaginationInput = true;

    let pdfLib;
    let pdfDocument;

    const dispatch = createEventDispatcher();

    let error;
    /**
     * Set in error mode and dispatch the error event
     * @param {Error} err
     */
    function handleError(err) {
        if (err instanceof TypeError) {
            error = err.message;
        } else if (pdfLib && err instanceof pdfLib.MissingPDFException) {
            error = __('Missing PDF file %s', src);
        } else if (pdfLib && err instanceof pdfLib.InvalidPDFException) {
            error = __('Invalid or corrupted PDF file.');
        } else {
            error = __('An error occurred while loading the PDF.');
        }
        dispatch('error', err);
    }

    let ready = false;
    /**
     * The viewer becomes ready
     */
    function onReady() {
        ready = true;
        dispatch('ready');
    }

    /**
     * Render the document as the initial render.
     * The function is debounced to be used by resize events
     */
    const reRender = debounce(() => {
        scale = 1;
        initialScaleDefined = false;
        render();
    }, 100);

    //observe size change on the container
    const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
            if (canvasElement) {
                reRender();
            }
        });
    });

    let renderingPromise = Promise.resolve();
    /**
     * Render the current page of the document, using the given scale
     * @returns {Promise|void}
     */
    function render() {
        if (pdfDocument) {
            if (page < 1 || page > pageCount) {
                page = 1;
            }
            return renderingPromise.then(() => {
                ready = false;
                renderingPromise = pdfDocument
                    .getPage(page)
                    .then(pdfPage => {
                        let viewport = pdfPage.getViewport({ scale });
                        if (!initialScaleDefined) {
                            const containerBBox = containerElement.getBoundingClientRect();
                            if (viewport.width > containerBBox.width) {
                                scale = (containerBBox.width - remToPx(4)) / viewport.width;
                                viewport = pdfPage.getViewport({ scale });
                            }
                            displayPaginationInput = containerBBox.width >= fullBarMiniumContainerWidth;
                            initialScaleDefined = true;
                        }

                        canvasElement.width = viewport.width;
                        canvasElement.height = viewport.height;

                        const canvasContext = canvasElement.getContext('2d');
                        return pdfPage
                            .render({
                                canvasContext,
                                viewport
                            })
                            .promise.then(() => pdfPage.getTextContent())
                            .then(textContent => {
                                const bbox = canvasElement.getBoundingClientRect();
                                textLayerElement.style.width = `${bbox.width}px`;
                                textLayerElement.style.height = `${bbox.height}px`;

                                textLayerElement.innerHTML = '';
                                return pdfLib.renderTextLayer({
                                    textContent: textContent,
                                    container: textLayerElement,
                                    viewport: viewport,
                                    textDivs: []
                                });
                            })
                            .then(() => (ready = true));
                    })
                    .catch(handleError);
                return renderingPromise;
            });
        }
    }

    onMount(() => {
        if (!src) {
            handleError(new TypeError(__('Missing PDF file.')));
        } else {
            import('./pdf.js')
                .then(({ default: getPdfJS }) => {
                    pdfLib = getPdfJS(options.workerSrc);

                    return pdfLib
                        .getDocument(src)
                        .promise.then(pdfDoc => {
                            pdfDocument = pdfDoc;
                            pageCount = pdfDoc.numPages;
                            return render();
                        })
                        .then(() => {
                            onReady();
                            resizeObserver.observe(containerElement);
                        });
                })
                .catch(handleError);
        }
    });

    onDestroy(() => {
        resizeObserver.disconnect();
    });

    /**
     * Download the document
     */
    function download() {
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = src;
        a.target = '_blank';
        a.download = src;

        a.click();
    }

    /**
     * Update the rendered page
     * @param {number} newPage - the new page to render
     */
    function updatePage(newPage) {
        if (newPage !== page && newPage > 0 && newPage <= pageCount) {
            page = newPage;
            render().then(() => {
                dispatch('pagechange', page);
            });
        }
    }

    /**
     * Update the page scale
     * @param {number} newScale - the new scale
     */
    function updateScale(newScale) {
        if (newScale !== scale && newScale >= minScale && newScale <= maxScale) {
            scale = newScale;
            render();
        }
    }

    const previous = () => updatePage(Math.max(page - 1, 1));
    const next = () => updatePage(Math.min(page + 1, pageCount));
    const pageChange = e => updatePage(parseInt(e.detail.value));
    const zoomIn = () => updateScale(Math.min(scale + scaleSteps, maxScale));
    const zoomOut = () => updateScale(Math.max(scale - scaleSteps, minScale));
</script>

<style>
    .document-viewer {
        overflow: hidden;
    }
    [role='toolbar'] {
        display: flex;
        height: 5.5rem;
        & > div {
            flex: 1 1 auto;
            display: flex;

            &:last-child,
            &:first-child {
                flex: 0 0 10rem;
            }
            &:first-child {
                justify-content: flex-start;
            }
            &:last-child {
                justify-content: flex-end;
            }
        }
        & .pagination {
            justify-content: center;
            & > span {
                line-height: 5.5rem;
                margin: auto 2rem;
                white-space: nowrap;

                & :global(input) {
                    margin-left: 0.5rem;
                    margin-right: 0.5rem;
                }
            }
        }
    }
    .document-wrapper {
        position: relative;
        height: calc(100% - 5.5rem);
        overflow: auto;
        background-color: var(--color-bg-inverted-secondary-hover);

        &:not(.ready) > * {
            visibility: hidden;
        }

        &:focus {
            outline: none;
        }
        &:focus-visible {
            border: var(--border-medium) dotted var(--color-border-focus-inverted);
        }

        &.error > p {
            visibility: visible;
            color: var(--color-text-inverted);
            margin: 2rem auto;
            text-align: center;
        }

        & > canvas {
            position: relative;
            display: block;
            margin: 2rem auto;
            box-shadow: 1px 1px 1px var(--color-shadow-default);
        }
        & .text-layer {
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
            display: block;
            margin: 2rem auto;
            box-shadow: 1px 1px 1px transparent;
            opacity: 0.6;
            line-height: 1;
        }

        & .text-layer > :global(span) {
            color: transparent;
            position: absolute;
            white-space: pre;
            cursor: text;
            transform-origin: 0% 0%;

            &::selection {
                /* custom color, to work with the transparency layer */
                background-color: hsl(208, 100%, 80%, 0.7);
                color: transparent;
            }
        }
    }

    @media screen and (--mq-maxwidth-small) {
        [role='toolbar'] .pagination > span {
            display: none;
        }
    }
</style>

<div
    class="document-viewer inverted"
    role="document"
    aria-label={title}
    style="width:{width}; height:{height};"
    bind:this={containerElement}
    {...$$restProps}>
    <div aria-label={__('Document toolbar')} role="toolbar" aria-controls={documentWrapperId}>
        <div>
            <IconBarButton
                label={__('Zoom in')}
                dataTestId="doc-zoom-in"
                size="base-16"
                icon="zoom-in-16"
                on:click={zoomIn}
                disabled={!ready || scale >= maxScale} />
            <IconBarButton
                label={__('Zoom out')}
                dataTestId="doc-zoom-out"
                size="base-16"
                icon="zoom-out-16"
                on:click={zoomOut}
                disabled={!ready || scale <= minScale} />
        </div>
        <div class="pagination">
            {#if pageCount > 1}
                <IconBarButton
                    label={__('Previous')}
                    dataTestId="doc-previous"
                    size="base-16"
                    icon="chevron-left-16"
                    iconRtlFlip={true}
                    on:click={previous}
                    disabled={!ready || page <= 1} />
                {#if displayPaginationInput}
                    <span>
                        <Input
                            name="page"
                            height="x-small"
                            size={1}
                            value={page}
                            on:change={pageChange}
                            placeholder="0"
                            disabled={!ready} />
                        {__('of %d', pageCount)}
                    </span>
                {/if}
                <IconBarButton
                    label={__('Next')}
                    dataTestId="doc-next"
                    size="base-16"
                    icon="chevron-right-16"
                    iconRtlFlip={true}
                    on:click={next}
                    disabled={!ready || page >= pageCount} />
            {/if}
        </div>
        <div>
            <IconBarButton
                label={__('Download')}
                dataTestId="doc-download"
                size="base-16"
                icon="download-16"
                on:click={download}
                disabled={!ready} />
        </div>
    </div>
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div class="document-wrapper" id={documentWrapperId} role="document" tabindex="0" class:ready class:error>
        {#if error}
            <p>{error}</p>
        {:else}
            <canvas bind:this={canvasElement} />
            <div class="text-layer" bind:this={textLayerElement} />
        {/if}
    </div>
</div>
